// @file Vuex store to manage users' collections of walls.
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { buildSlideshowLink } from '@@/bits/slideshow'
import { PageType } from '@@/types/slideshow'
import type { JsonAPIResource } from '@padlet/arvo'
import { fetchResponse, HTTPMethod } from '@padlet/fetch'
import { copyToClipboard } from 'bits/clipboard'
import {
  filterDisplayName,
  galleryVizes,
  getCollectionFetchLimits,
  getEmptyFilterCollectionCopy,
  getEmptyFolderCollectionCopy,
  LIBRARY_ACCOUNT_FILTERS,
  TENANT_USER_ACCOUNT_FILTERS,
  USER_ACCOUNT_FILTERS,
} from 'bits/collections_helper'
import getCsrfToken from 'bits/csrf_token'
import fetchElectronBridge from 'bits/electron_bridge'
import { captureException, captureMessage } from 'bits/error_tracker'
import { isAppUsing } from 'bits/flip'
import { __ } from 'bits/intl'
import {
  buildUrlFromPath,
  currentHostname,
  currentHostWithProtocol,
  navigateTo,
  transformCurrentUrl,
} from 'bits/location'
import PromiseQueue from 'bits/promise_queue'
import safeStorage from 'bits/safe_storage'
import { NATIVE_HOST } from 'bits/url'
import { vDel, vSet } from 'bits/vue'
import { Folder as FolderApi, UserGroups as UserGroupsApi, Wall as WallApi } from 'dashboard/padlet_api'
import { SnackbarNotificationType } from 'enums'
import { cloneDeep, keyBy, mapValues, sortBy, uniq } from 'lodash-es'
import { fetchJson } from 'surface/api_fetch'
import PadletApi from 'surface/padlet_api'
import type {
  AccountKey,
  Folder,
  FolderId,
  Id,
  LibraryId,
  SharedCollection,
  SharedCollectionAccountKey,
  UserGroup,
  UserGroupFolderApiResponse,
  UserGroupId,
  UserId,
  WallCamelCase as Wall,
  WallId,
  WallViz,
} from 'types'
import type { CollectionKey, WallsFilter, WallSortAttribute, WallSortDirection } from 'types/collections'
import { CollectionKeyTypes } from 'types/collections'
import type { Module } from 'vuex'
import { shouldLog } from 'vuexstore/plugins/logger'

/***********************************
 * General helpers
 ***********************************/

// Logger helper
function devLog(message, ...args): void {
  const logThisModule = false
  if (!logThisModule || !shouldLog) return
  console.log(`Vuex collections store: ${message}`, ...args) // goodcheck-disable-line
}

// Avoid race conditions that cause walls and folders to dis/appear etc unexpectedly.
const q = new PromiseQueue()

// Returns false if any value of `hash` is blank. Logs bad values.
export function assertPresent(hash: Record<string, unknown>): boolean {
  let allPresent = true
  Object.entries(hash).forEach(([key, value]) => {
    if (value !== null && value !== undefined) return
    captureMessage(`Expected ${key} to have been set, but it was not.`)
    allPresent = false
  })
  return allPresent
}

/***********************************
 * Types
 ***********************************/

interface CollectionsTree<T> {
  filter: Partial<Record<WallsFilter, T>>
  folderId: Record<FolderId, T>
  groupFolderId: Record<UserGroupId, T>
  search: Record<string, T>
}
type CollectionsSubtree<T> =
  | Partial<Record<WallsFilter, T>>
  | Record<FolderId, T>
  | Record<UserGroupId, T>
  | Record<string, T>

/*************************
 * Constants and such
 *************************/

const DEFAULT_COLLECTION_KEY: CollectionKey = { typeKey: 'filter', indexKey: 'made' }

/***********************************
 * CollectionsTree and helpers
 ***********************************/

function getCollectionValue<T>(collectionTree: CollectionsTree<T>, collectionKey: CollectionKey): T | null {
  const collectionTypeSubtree = collectionTree[collectionKey.typeKey]
  return collectionTypeSubtree ? collectionTypeSubtree[collectionKey.indexKey] : null
}

function getAccountCollectionValue<T>(
  accountsCollectionsTree: Record<Id, CollectionsTree<T>>,
  accountId: Id | string | null,
  collectionKey: CollectionKey,
): T | null {
  if (!accountId) return null
  accountId = Number(accountId)
  const collectionTree = accountsCollectionsTree[accountId]
  return collectionTree ? getCollectionValue(collectionTree, collectionKey) : null
}

function transformCollectionValue<T>(
  collectionTree: CollectionsTree<T>,
  collectionKey: CollectionKey,
  transform: (oldValue: T | null) => T,
): void {
  const oldCollectionTypeSubtree = collectionTree[collectionKey.typeKey] ?? {}
  const oldValue = oldCollectionTypeSubtree[collectionKey.indexKey] ?? null
  const newValue = transform(oldValue)
  const newCollectionTypeSubtree = { ...oldCollectionTypeSubtree, [collectionKey.indexKey]: newValue }
  vSet(collectionTree, collectionKey.typeKey, newCollectionTypeSubtree)
}

function transformAccountCollectionValue<T>(
  accountsCollectionsTree: Record<Id, CollectionsTree<T>>,
  accountId: Id | string | null,
  collectionKey: CollectionKey,
  transform: (oldValues: T | null) => T,
): void {
  if (!accountId) return
  accountId = Number(accountId)
  const collectionTree = accountsCollectionsTree[accountId] ?? vSet(accountsCollectionsTree, accountId, {})
  transformCollectionValue(collectionTree, collectionKey, transform)
}

function transformAccountsCollectionsValues<T>(
  accountsCollectionsTree: Record<Id, CollectionsTree<T>>,
  transform: (oldValues?: T) => T,
): void {
  Object.values(accountsCollectionsTree).forEach((collectionsValues) => {
    Object.values(collectionsValues).forEach((collectionTypeSubtree: CollectionsSubtree<T>) => {
      Object.entries(collectionTypeSubtree).forEach(([collectionKeyIndex, values]) => {
        const newValues = transform(values)
        vSet(collectionTypeSubtree, collectionKeyIndex, newValues)
      })
    })
  })
}

function setAccountCollectionValue<T>(
  accountsCollectionsTree: Record<Id, CollectionsTree<T>>,
  accountId: Id | string,
  collectionKey: CollectionKey,
  value: T,
): void {
  transformAccountCollectionValue(accountsCollectionsTree, accountId, collectionKey, () => value)
}

function transformUserCollectionType<T>(
  state: CollectionsStoreModule,
  userId: UserId,
  collectionKeyType: CollectionKeyTypes,
  transform: (subtree: CollectionsSubtree<T>) => CollectionsSubtree<T>,
): void {
  if (!userId) return
  const collectionTree = state.usersCollectionsWallIds[userId] ?? vSet(state.usersCollectionsWallIds, userId, {})
  vSet(collectionTree, collectionKeyType, transform(collectionTree[collectionKeyType]))
}

// emojiRegex obtained from unicode site https://unicode.org/reports/tr51/#EBNF_and_Regex
function extractEmojiFromFolderName(name: string): string {
  const emojiRegex =
    /^(\p{RI}\p{RI}|\p{Extended_Pictographic}(\p{Emoji_Modifier}|\u{FE0F}\u{20E3}?|[\u{E0020}-\u{E007E}]+\u{E007F})?(\u{200D}(\p{RI}\p{RI}|\p{Emoji}(\p{Emoji_Modifier}|\u{FE0F}\u{20E3}?|[\u{E0020}-\u{E007E}]+\u{E007F})?))*)/u
  const matches = name.match(emojiRegex)
  if (matches === null) return ''
  return matches[0]
}

/***********************************
 * CollectionsStoreModule helpers
 ***********************************/

export interface CollectionsStoreModule {
  // By ID
  wallsById: Record<WallId, Wall>
  foldersById: Record<FolderId, Folder>
  groupsById: Record<UserGroupId, Folder>
  // User Collections
  isUsersCollectionsFetchErrored: Record<UserId, CollectionsTree<boolean>>
  isUsersCollectionsFetched: Record<UserId, CollectionsTree<boolean>>
  isUsersCollectionsFetching: Record<UserId, CollectionsTree<boolean>>
  usersCollectionsWallIds: Record<UserId, CollectionsTree<WallId[]>>
  // Library Collections
  isLibrariesCollectionsFetchErrored: Record<LibraryId, CollectionsTree<boolean>>
  isLibrariesCollectionsFetched: Record<LibraryId, CollectionsTree<boolean>>
  isLibrariesCollectionsFetching: Record<LibraryId, CollectionsTree<boolean>>
  librariesCollectionsWallIds: Record<LibraryId, CollectionsTree<WallId[]>>
  // Shared Collections
  sharedCollections: SharedCollection[]
  // Cursors
  activeSharedCollectionAccountKey: SharedCollectionAccountKey | null
  activeAccountKeyCursor: AccountKey | null
  activeUserIdCursor: UserId | null
  activeCollectionKeyCursor: CollectionKey
  activeWallIdCursor: WallId | null
  activeSortAttribute: WallSortAttribute
  activeSortDirection: WallSortDirection | null
  activeSearchQuery: string
  galleryViz: WallViz | 'all'
}

const buildBlankState = (): CollectionsStoreModule => ({
  // By ID
  wallsById: {},
  foldersById: {},
  groupsById: {},
  // User Collections
  isUsersCollectionsFetchErrored: {},
  isUsersCollectionsFetched: {},
  isUsersCollectionsFetching: {},
  usersCollectionsWallIds: {},
  // Library Collections
  isLibrariesCollectionsFetchErrored: {},
  isLibrariesCollectionsFetched: {},
  isLibrariesCollectionsFetching: {},
  librariesCollectionsWallIds: {},
  // Shared Collections
  sharedCollections: [],
  // Cursors
  activeSharedCollectionAccountKey: null,
  activeAccountKeyCursor: null,
  activeUserIdCursor: null,
  activeCollectionKeyCursor: DEFAULT_COLLECTION_KEY,
  activeWallIdCursor: null,
  activeSortAttribute: 'date',
  activeSortDirection: null,
  activeSearchQuery: '',
  galleryViz: 'all',
})

/*************************
 * The Store
 *************************/

const collectionsStore: Module<CollectionsStoreModule, object> = {
  namespaced: true,
  state: buildBlankState,
  getters: {
    isCollectionBookmark(_state, getters, _rootState, rootGetters) {
      return ({ typeKey, indexKey }: CollectionKey): boolean => {
        if (indexKey === 'favorites' || typeKey === 'folderId') return true
        return false
      }
    },
    isCollectionCrossLibrary(_state, getters) {
      return (collectionKey: CollectionKey): boolean => {
        if (getters.isCollectionBookmark(collectionKey) || getters.isCollectionSharedCollection(collectionKey))
          return true
        return ['combined_recents', 'combined_shared', 'gallery'].includes(collectionKey.indexKey as string)
      }
    },
    isCollectionSharedCollection(_state) {
      return (collectionKey: CollectionKey): boolean => {
        return ['combined_shared_user', 'combined_shared_library'].includes(collectionKey.indexKey as string)
      }
    },
    isCollectionShared(_state) {
      return (collectionKey: CollectionKey): boolean => {
        return ['combined_shared_user', 'combined_shared_library', 'combined_shared'].includes(
          collectionKey.indexKey as string,
        )
      }
    },
    isActiveCollectionShared(state, getters): boolean {
      return getters.isCollectionShared(getters.activeCollectionKey)
    },
    isActiveCollectionSharedCollection(state, getters): boolean {
      return getters.isCollectionSharedCollection(getters.activeCollectionKey)
    },
    isActiveCollectionCrossLibrary(state, getters): boolean {
      return getters.isCollectionCrossLibrary(getters.activeCollectionKey)
    },
    isActiveCollectionBookmark(_state, getters): boolean {
      return getters.isCollectionBookmark(getters.activeCollectionKey)
    },
    isActiveCollectionFetching(state, getters): boolean | null {
      if (!getters.activeAccountKey) return true
      const { type, id } = getters.activeAccountKey
      const isLibrary = type === 'library'
      const isFetching = isLibrary ? state.isLibrariesCollectionsFetching : state.isUsersCollectionsFetching
      return getAccountCollectionValue(isFetching, id, getters.activeCollectionKey)
    },
    isActiveCollectionFetched(state, getters): boolean | null {
      if (!getters.activeAccountKey) return false
      const { type, id } = getters.activeAccountKey
      const isLibrary = type === 'library'
      const isFetched = isLibrary ? state.isLibrariesCollectionsFetched : state.isUsersCollectionsFetched
      return getAccountCollectionValue(isFetched, id, getters.activeCollectionKey)
    },
    isActiveCollectionFetchErrored(state, getters): boolean | null {
      if (!getters.activeAccountKey) return false
      const { type, id } = getters.activeAccountKey
      const isLibrary = type === 'library'
      const isErrored = isLibrary ? state.isLibrariesCollectionsFetchErrored : state.isUsersCollectionsFetchErrored
      return getAccountCollectionValue(isErrored, id, getters.activeCollectionKey)
    },
    isCollectionFetching(state, getters): Function {
      return (collectionKey: CollectionKey): boolean | null =>
        getAccountCollectionValue(state.isUsersCollectionsFetching, getters.activeUserId, collectionKey)
    },
    isCollectionFetched(state, getters): Function {
      return (collectionKey: CollectionKey): boolean | null =>
        getAccountCollectionValue(state.isUsersCollectionsFetched, getters.activeUserId, collectionKey)
    },
    isCollectionFetchErrored(state, getters): Function {
      return (collectionKey: CollectionKey): boolean | null =>
        getAccountCollectionValue(state.isUsersCollectionsFetchErrored, getters.activeUserId, collectionKey)
    },
    foldersById(state): Record<FolderId, Folder> {
      return state.foldersById
    },
    foldersArray(state): Folder[] {
      return Object.values(state.foldersById)
    },
    activeSharedCollectionAccountKey(state): SharedCollectionAccountKey | null {
      return state.activeSharedCollectionAccountKey
    },
    activeAccountKey(state): AccountKey | null {
      return state.activeAccountKeyCursor
    },
    activeUserId(state): UserId | null {
      return state.activeUserIdCursor
    },
    activeWallId(state): WallId | null {
      return state.activeWallIdCursor
    },
    activeWall(state): Wall | null {
      return state.activeWallIdCursor ? state.wallsById[state.activeWallIdCursor] : null
    },
    activeUserFolderIds(state, getters): FolderId[] {
      const userId = getters.activeUserId
      const foldersTree = state.usersCollectionsWallIds[userId]?.folderId ?? {}
      return Object.keys(foldersTree).map((folderId) => parseInt(folderId))
    },
    activeUserFolders(state, getters): Folder[] {
      // Admins can access any folder in the tenant, including those owned by others.
      // We need to filter those out.
      const folders = getters.activeUserFolderIds
        .map((id: FolderId) => state.foldersById[id])
        .filter((folder: Folder) => folder)
        .map((folder) => {
          const trimmedFolderName = folder.name.trim()

          // We attempt to get the folder icon if the folder
          // does not have an icon
          const emoji = extractEmojiFromFolderName(trimmedFolderName)
          if (!folder.icon && emoji) {
            return { ...folder, icon: emoji, name: trimmedFolderName.substring(emoji.length) }
          }
          return folder
        })
      return sortBy(folders, (folder) => folder.name.toLowerCase())
    },
    activeUserGroups(state, getters): UserGroup[] {
      const groups = getters.activeUserGroupIds
        .map((id: UserGroupId) => state.groupsById[id])
        .filter((folder: UserGroup) => folder)

      return sortBy(groups, (folder) => folder.name.toLowerCase())
    },
    activeUserGroupIds(state, getters): UserGroupId[] {
      const userId = getters.activeUserId
      const foldersTree = state.usersCollectionsWallIds[userId]?.groupFolderId ?? {}
      return Object.keys(foldersTree).map((groupId) => parseInt(groupId))
    },
    activeUserGroup(state, getters): UserGroup | null {
      const collectionKey = getters.activeCollectionKey
      if (collectionKey.typeKey !== CollectionKeyTypes.GroupFolderId) return null
      return state.groupsById[collectionKey.indexKey]
    },
    activeCollectionKey(state, getters): CollectionKey {
      const collectionKeyCursor = state.activeCollectionKeyCursor

      // If activeFolderId populated from local storage is not valid for this user, ignore it for now.
      if (
        collectionKeyCursor.typeKey === 'folderId' &&
        getters.activeUserFolderIds.find((id: FolderId) => id == collectionKeyCursor.indexKey)
      ) {
        return collectionKeyCursor
      } else if (
        collectionKeyCursor.typeKey === CollectionKeyTypes.GroupFolderId &&
        getters.activeUserGroupIds.find((id: UserGroupId) => String(id) === String(collectionKeyCursor.indexKey)) !==
          undefined
      ) {
        return collectionKeyCursor
      } else if (collectionKeyCursor.typeKey === 'filter') {
        return collectionKeyCursor
      } else {
        return DEFAULT_COLLECTION_KEY
      }
    },
    isActiveCollectionKey(state, getters): (key: CollectionKey) => boolean {
      return (key: CollectionKey): boolean => {
        const activeKey = getters.activeCollectionKey
        return key.typeKey === activeKey.typeKey && key.indexKey === activeKey.indexKey
      }
    },
    isActiveFolderId(state, getters): (folderId: FolderId) => boolean {
      return (folderId: FolderId): boolean => {
        return getters.isActiveCollectionKey({ typeKey: 'folderId', indexKey: folderId })
      }
    },
    isActiveGroupId(_, getters): (groupId: UserGroupId) => boolean {
      return (groupId: UserGroupId): boolean => {
        return getters.isActiveCollectionKey({ typeKey: CollectionKeyTypes.GroupFolderId, indexKey: groupId })
      }
    },
    isActiveFilter(state, getters): (filter: WallsFilter) => boolean {
      return (filter: WallsFilter): boolean => {
        return getters.isActiveCollectionKey({ typeKey: 'filter', indexKey: filter })
      }
    },
    activeFolder(state, getters): Folder | null {
      const collectionKey = getters.activeCollectionKey
      if (collectionKey.typeKey !== 'folderId') return null
      return state.foldersById[collectionKey.indexKey]
    },
    activeFolderId(state, getters): FolderId | null {
      return getters.activeFolder?.id || null
    },
    activeCollectionWalls(state, getters): Wall[] {
      if (!getters.activeAccountKey) return []
      const { type, id } = getters.activeAccountKey
      const activeCollectionKey = getters.activeCollectionKey
      const isLibrary = type === 'library'

      // Backend filtering
      const wallIdsTree = isLibrary ? state.librariesCollectionsWallIds : state.usersCollectionsWallIds
      const wallIds = getAccountCollectionValue(wallIdsTree, id, activeCollectionKey) ?? []
      // Folder wallIds might include IDs for walls that are not yet loaded.
      const walls = wallIds.map((wallId: WallId) => state.wallsById[wallId]).filter((wall: Wall) => wall)

      // Frontend filtering
      const identityFilter = (): true => true
      const galleryFilter = (wall: Wall): boolean => {
        const viz = state.galleryViz
        return viz === 'all' || viz === wall.viz
      }
      const currentFilter = activeCollectionKey.indexKey === 'gallery' ? galleryFilter : identityFilter
      const frontendFilteredWalls = walls.filter(currentFilter)

      // only sort on the frontend when all walls have been fetched
      if (getters.isActiveCollectionFetched) {
        const sorter = {
          name: (wall: Wall): string => (wall.title || '').trim(),
          date: (wall: Wall): number => +new Date(wall[getters.activeCollectionDateSortAttribute]),
        }[state.activeSortAttribute]
        const sortedFrontendFilteredWalls = sortBy(frontendFilteredWalls, sorter)
        if (getters.activeSortDirection === 'desc') sortedFrontendFilteredWalls.reverse()
        return sortedFrontendFilteredWalls
      }

      return frontendFilteredWalls
    },
    activeSortAttribute(state): WallSortAttribute {
      return state.activeSortAttribute
    },
    activeCollectionDateSortAttribute(state, getters): string {
      return (
        {
          combined_recents: 'lastPresentAt',
          gallery: 'createdAt',
          trashed: 'trashedAt',
        }[getters.activeCollectionKey.indexKey] || 'updatedAt'
      )
    },
    activeCollectionDateSortAttributeText(state, getters): string {
      return (
        {
          createdAt: __('Date created'),
          updatedAt: __('Last modified'),
          lastPresentAt: __('Last viewed'),
          trashedAt: __('Deletion date'),
        }[getters.activeCollectionDateSortAttribute] || __('Date')
      )
    },
    activeSortDirection(state, getters): WallSortDirection {
      return state.activeSortDirection || (getters.activeSortAttribute === 'date' ? 'desc' : 'asc')
    },
    activeCollectionName(state, getters): string {
      if (getters.activeFolder) return getters.activeFolder.name
      if (getters.activeUserGroup) return getters.activeUserGroup.name
      if (getters.isActiveCollectionSharedCollection) {
        return getters.sharedCollections.find((collection) => {
          return (
            collection.id === getters.activeSharedCollectionAccountKey?.id &&
            collection.type === getters.activeSharedCollectionAccountKey?.type
          )
        }).name
      }
      if (getters.isActiveCollectionShared && getters.isSharedCollectionsEmpty) {
        return __('Shared')
      }
      if (getters.isActiveFilter('gallery')) {
        return galleryVizes.find((vizData) => vizData.viz === getters.galleryViz)?.text || ''
      }
      const filter = getters.activeCollectionKey.indexKey

      return filterDisplayName(filter)
    },
    // Returns ids of the folders that the activeWall is contained in, as viewed by the activeUser.
    activeWallFolderIds(state, getters): FolderId[] {
      const wallId = getters.activeWallId
      const userId = getters.activeUserId
      if (!userId || !wallId) return []

      const foldersHash = state.usersCollectionsWallIds[userId]?.folderId
      if (!foldersHash) return []

      const containingFolderIds = getters.activeUserFolderIds.filter((folderId: FolderId) => {
        const isWallInFolder = foldersHash[folderId]?.find((id) => id === wallId)
        return isWallInFolder
      })
      return containingFolderIds
    },
    wallFiltersForAccountCollection(state, getters, rootState, rootGetters) {
      return ({ accountKey, collectionKey }): WallsFilter[] => {
        if (!accountKey || !collectionKey) return []

        // Dont' show for any of the other cross-library collections
        if (getters.isCollectionCrossLibrary(collectionKey)) return []

        // Library accounts
        if (accountKey.type === 'library') return getters.libraryAccountFilters

        // Non-alexandria user account
        const isNativeHost = currentHostname().endsWith(NATIVE_HOST)
        return isNativeHost ? getters.userAccountFilters : getters.tenantUserAccountFilters
      }
    },
    activeAccountWallFilters(state, getters): WallsFilter[] {
      const accountKey = getters.activeAccountKey
      const collectionKey = getters.activeCollectionKey
      return getters.wallFiltersForAccountCollection({ accountKey, collectionKey })
    },
    isWallIdInFavorites(state, getters) {
      return (wallId: WallId): boolean => {
        const userId = getters.activeUserId
        if (!userId || !wallId) return false

        const filtersHash = state.usersCollectionsWallIds[userId]?.filter ?? {}
        const favoritesWallIds = filtersHash.favorites ?? []
        return favoritesWallIds.includes(wallId)
      }
    },
    isActiveWallInFavorites(state, getters): boolean {
      return getters.isWallIdInFavorites(getters.activeWallId)
    },
    isWallIdBookmarked(state, getters) {
      return (wallId: WallId): boolean => {
        if (!wallId) return false

        const isInFavorites = getters.isWallIdInFavorites(wallId)
        if (isInFavorites) return true

        const userId = getters.activeUserId
        if (!userId) return false

        const foldersHash = state.usersCollectionsWallIds[userId]?.folderId ?? {}
        const containingFolder = Object.values(foldersHash).find((folderWallIds) => folderWallIds.includes(wallId))
        return !(containingFolder == null)
      }
    },
    isActiveWallBookmarked(state, getters): boolean {
      return getters.isWallIdBookmarked(getters.activeWallId)
    },
    isActiveWallInsideLibrary(state, getters): boolean {
      return !!getters.activeWall.libraryId
    },
    wallsByCollection(state, getters) {
      return (collectionKey: CollectionKey): Wall[] => {
        const userId = getters.activeUserId

        const wallIds = getAccountCollectionValue(state.usersCollectionsWallIds, userId, collectionKey) ?? []
        return wallIds.map((wallId) => state.wallsById[wallId])
      }
    },
    activeSearchQuery(state): string {
      return state.activeSearchQuery
    },
    isActiveSearchQueryEmpty(state): boolean {
      return state.activeSearchQuery === ''
    },
    emptyActiveCollectionCopy(state, getters): [string, string] {
      const { typeKey, indexKey }: CollectionKey = getters.activeCollectionKey
      if (typeKey === CollectionKeyTypes.Filter) return getEmptyFilterCollectionCopy(indexKey)
      if (typeKey === CollectionKeyTypes.FolderId) return getEmptyFolderCollectionCopy()
      return ['', '']
    },
    sharedCollections(state): SharedCollection[] {
      return state.sharedCollections
    },
    isSharedCollectionsEmpty(state): boolean {
      return state.sharedCollections.length === 0
    },
    galleryViz(state): WallViz | 'all' {
      return state.galleryViz
    },
    libraryAccountFilters(): WallsFilter[] {
      return LIBRARY_ACCOUNT_FILTERS
    },
    userAccountFilters(): WallsFilter[] {
      return USER_ACCOUNT_FILTERS
    },
    tenantUserAccountFilters(): WallsFilter[] {
      return TENANT_USER_ACCOUNT_FILTERS
    },
    wallIdsByFolderId(state, getters): Record<FolderId, WallId[]> {
      return state.usersCollectionsWallIds[getters.activeUserId]?.folderId ?? {}
    },
    favoritesFolderWallIds(state, getters): WallId[] {
      const filtersHash = state.usersCollectionsWallIds[getters.activeUserId]?.filter ?? {}
      return filtersHash.favorites ?? []
    },
  },
  mutations: {
    INITIALIZE_STATE(state, initialState: Partial<CollectionsStoreModule>): void {
      Object.assign(state, initialState)
    },

    // CURSORS

    SET_ACCOUNT_COLLECTION_CURSOR(state, { accountKey, collectionKey }): void {
      state.activeAccountKeyCursor = accountKey
      state.activeCollectionKeyCursor = collectionKey
    },
    SET_ACTIVE_USER_CURSOR(state, userId: UserId): void {
      state.activeUserIdCursor = userId
    },
    SET_ACTIVE_SORT_ATTRIBUTE(state, sortAttribute: WallSortAttribute): void {
      state.activeSortAttribute = sortAttribute
      // Use default sort direction for new attribute
      state.activeSortDirection = null
    },
    SET_ACTIVE_SORT_DIRECTION(state, sortDirection: WallSortDirection): void {
      state.activeSortDirection = sortDirection
    },
    SET_ACTIVE_WALL_CURSOR(state, wallId: WallId): void {
      state.activeWallIdCursor = wallId
    },

    // FOLDERS

    CACHE_FOLDERS(state, { folders }): void {
      state.foldersById = { ...state.foldersById, ...keyBy(folders, 'id') }
    },
    UPDATE_FOLDER_LIST_FOR_USER(state, { userId, folderIds }): void {
      transformUserCollectionType(state, userId, CollectionKeyTypes.FolderId, (oldFoldersState) => {
        oldFoldersState = oldFoldersState ?? {}
        return folderIds.reduce((newFoldersState, folderId) => {
          newFoldersState[folderId] = oldFoldersState[folderId] ?? []
          return newFoldersState
        }, {})
      })
    },
    ADD_FOLDER_FOR_USER(state, { userId, folderId }): void {
      const addFolderId = (oldFoldersState): CollectionsSubtree<WallId[]> => ({ ...oldFoldersState, [folderId]: [] })
      transformUserCollectionType(state, userId, CollectionKeyTypes.FolderId, addFolderId)
    },
    REMOVE_FOLDER_FOR_ALL_USERS(state, { folderId }): void {
      Object.values(state.usersCollectionsWallIds).forEach((userCollectionsWallIds) => {
        vDel(userCollectionsWallIds.folderId, folderId)
      })
      vDel(state.foldersById, folderId)
    },

    // USER GROUPS
    CACHE_GROUPS(state, { groups }): void {
      state.groupsById = { ...state.groupsById, ...keyBy(groups, 'id') }
    },
    UPDATE_GROUP_LIST_FOR_USER(state, { userId, groupIds }): void {
      transformUserCollectionType(state, userId, CollectionKeyTypes.GroupFolderId, (oldGroupsState) => {
        oldGroupsState = oldGroupsState ?? {}
        return groupIds.reduce((newGroupsState, groupId) => {
          newGroupsState[groupId] = oldGroupsState[groupId] ?? []
          return newGroupsState
        }, {})
      })
    },

    // FETCH COLLECTION WALLS

    FETCH_WALLS_START(state, { accountKey: { type, id }, collectionKey }): void {
      const isLibrary = type === 'library'
      const isErrored = isLibrary ? state.isLibrariesCollectionsFetchErrored : state.isUsersCollectionsFetchErrored
      const isFetching = isLibrary ? state.isLibrariesCollectionsFetching : state.isUsersCollectionsFetching
      // Reset error to false only if previous fetch resulted in error for this collectionKey
      if (getAccountCollectionValue(isErrored, id, collectionKey)) {
        setAccountCollectionValue(isErrored, id, collectionKey, false)
      }
      setAccountCollectionValue(isFetching, id, collectionKey, true)
    },
    FETCH_WALLS_ERROR(state, { accountKey: { type, id }, collectionKey }): void {
      const isLibrary = type === 'library'
      const isErrored = isLibrary ? state.isLibrariesCollectionsFetchErrored : state.isUsersCollectionsFetchErrored
      const isFetching = isLibrary ? state.isLibrariesCollectionsFetching : state.isUsersCollectionsFetching
      setAccountCollectionValue(isFetching, id, collectionKey, false)
      setAccountCollectionValue(isErrored, id, collectionKey, true)
    },
    FETCH_WALLS_HALT(state, { accountKey: { type, id }, collectionKey }): void {
      const isLibrary = type === 'library'
      const isFetching = isLibrary ? state.isLibrariesCollectionsFetching : state.isUsersCollectionsFetching
      setAccountCollectionValue(isFetching, id, collectionKey, false)
    },
    FETCH_WALLS_COMPLETE(state, { accountKey: { type, id }, collectionKey }): void {
      const isLibrary = type === 'library'
      const isFetching = isLibrary ? state.isLibrariesCollectionsFetching : state.isUsersCollectionsFetching
      const isFetched = isLibrary ? state.isLibrariesCollectionsFetched : state.isUsersCollectionsFetched
      setAccountCollectionValue(isFetched, id, collectionKey, true)
      setAccountCollectionValue(isFetching, id, collectionKey, false)
    },

    // WALL MUTATIONS

    CACHE_WALLS(state, { walls }): void {
      state.wallsById = { ...state.wallsById, ...keyBy(walls, 'id') }
    },
    UPDATE_WALL_ATTRIBUTE(
      state,
      {
        wallId,
        targetAttribute,
        newAttributeValue,
      }: {
        wallId: WallId
        targetAttribute: keyof Wall
        newAttributeValue: Wall[keyof Wall]
      },
    ): void {
      let targetWall = state.wallsById[wallId]
      targetWall = { ...targetWall, [targetAttribute]: newAttributeValue }
      state.wallsById = { ...state.wallsById, [wallId]: { ...targetWall } }
    },

    // COLLECTION MUTATIONS

    SET_COLLECTION_WALL_IDS(
      state,
      {
        accountKey: { type, id },
        collectionKey,
        wallIds,
      }: { accountKey: AccountKey; collectionKey: CollectionKey; wallIds: WallId[] },
    ): void {
      const isLibrary = type === 'library'
      const wallIdsTree = isLibrary ? state.librariesCollectionsWallIds : state.usersCollectionsWallIds
      setAccountCollectionValue(wallIdsTree, id, collectionKey, wallIds)
    },
    ADD_WALL_IDS_TO_COLLECTION(state, { accountKey: { type, id }, collectionKey, wallIds }): void {
      const isLibrary = type === 'library'
      const wallIdsTree = isLibrary ? state.librariesCollectionsWallIds : state.usersCollectionsWallIds
      const addWallIds = (oldWallIds): WallId[] => uniq([...(oldWallIds ?? []), ...wallIds])
      transformAccountCollectionValue(wallIdsTree, id, collectionKey, addWallIds)
    },
    REMOVE_WALL_ID_FROM_COLLECTION(state, { accountKey: { type, id }, collectionKey, wallId }): void {
      const isLibrary = type === 'library'
      const wallIdsTree = isLibrary ? state.librariesCollectionsWallIds : state.usersCollectionsWallIds
      const removeWallId = (oldWallIds): WallId[] => {
        if (oldWallIds === null) {
          return []
        }

        return oldWallIds.filter((oldWallId) => oldWallId !== wallId)
      }
      transformAccountCollectionValue(wallIdsTree, id, collectionKey, removeWallId)
    },
    REMOVE_WALL_ID_FROM_ALL_COLLECTIONS(state, { wallId }): void {
      const transform = (wallIds?: WallId[]): WallId[] => (wallIds ?? []).filter((id) => id !== wallId)
      transformAccountsCollectionsValues<WallId[]>(state.usersCollectionsWallIds, transform)
      transformAccountsCollectionsValues<WallId[]>(state.librariesCollectionsWallIds, transform)
    },

    // SEARCH
    SET_ACTIVE_SEARCH_QUERY(state, { activeSearchQuery }): void {
      state.activeSearchQuery = activeSearchQuery
    },

    // SHARED
    SET_SHARED_COLLECTIONS(state, { sharedCollections }: { sharedCollections: SharedCollection[] }): void {
      state.sharedCollections = [...state.sharedCollections, ...sharedCollections]
    },
    SET_SHARED_COLLECTION_ACTIVE_KEY(
      state,
      { sharedCollectionAccountKey }: { sharedCollectionAccountKey: SharedCollectionAccountKey },
    ): void {
      state.activeSharedCollectionAccountKey = sharedCollectionAccountKey
    },

    // GALLERY
    SET_GALLERY_VIZ(state, viz): void {
      state.galleryViz = viz
    },
  },
  actions: {
    // CURSORS

    setAccountCollectionCursor({ commit }, { accountKey, collectionKey }): void {
      commit('SET_ACCOUNT_COLLECTION_CURSOR', { accountKey, collectionKey })
    },
    setActiveUser({ commit }, userId): void {
      commit('SET_ACTIVE_USER_CURSOR', userId)
    },
    setActiveWall({ commit }, wallId: WallId): void {
      commit('SET_ACTIVE_WALL_CURSOR', wallId)
    },
    unsetActiveWall({ commit }): void {
      commit('SET_ACTIVE_WALL_CURSOR', null)
    },

    // FOLDERS

    async fetchFolders({ getters, commit, dispatch }): Promise<Folder[]> {
      const userId = getters.activeUserId
      const accountKey = { type: 'user', id: userId }
      const folders: Folder[] = []

      try {
        const response = await fetchJson(`/api/1/folders`)
        response.data.forEach(({ attributes: { id, name, wallIds } }) => {
          folders.push({ id, name })
          const collectionKey: CollectionKey = { typeKey: 'folderId', indexKey: id }
          commit('SET_COLLECTION_WALL_IDS', { accountKey, collectionKey, wallIds })
        })
        commit('CACHE_FOLDERS', { folders })
        const folderIds = folders.map((folder) => folder.id)
        commit('UPDATE_FOLDER_LIST_FOR_USER', { userId, folderIds })
      } catch (error) {
        dispatch('genericFetchError', { error }, { root: true })
      }

      return folders
    },
    validateFolderNameUnique({ getters, dispatch }, name): { success: boolean } {
      const isNameTaken = getters.foldersArray.map((folder) => folder.name).includes(name?.trim())
      if (!isNameTaken) return { success: true }
      dispatch(
        'snackbar',
        { message: __('Name is taken by another folder. Please pick another name.') },
        { root: true },
      )
      return { success: false }
    },
    async createFolder(
      { commit, dispatch, getters },
      { userId, name },
    ): Promise<{ success: boolean; collectionKey?: CollectionKey }> {
      const { success }: { success: boolean } = await dispatch('validateFolderNameUnique', name)
      if (!success) return { success }

      try {
        const { data } = (await FolderApi.create({ name })) as { data: JsonAPIResource<Folder> }
        const folder = data.attributes
        commit('CACHE_FOLDERS', { folders: [folder] })
        commit('ADD_FOLDER_FOR_USER', { userId, folderId: folder.id })
        // Add current active wall (if any) to the new folder created
        getters.activeWallId && (await dispatch('addBookmark', { folderId: folder.id, wallId: getters.activeWallId }))
        const collectionKey: CollectionKey = { typeKey: 'folderId', indexKey: folder.id }
        return { success: true, collectionKey }
      } catch (error) {
        void dispatch('genericFetchError', { error }, { root: true })
        return { success: false }
      }
    },
    async createFolderForActiveUser(
      { getters, dispatch },
      { name },
    ): Promise<{ success: boolean; collectionKey?: CollectionKey }> {
      const userId = getters.activeUserId
      if (!assertPresent({ userId, name })) return { success: false }
      return await dispatch('createFolder', { userId, name })
    },
    async updateFolder({ state, commit, dispatch }, { folderId, name }): Promise<{ success: boolean }> {
      const isNoop = name?.trim() === state.foldersById[folderId]?.name
      if (isNoop) return { success: false }

      const { success }: { success: boolean } = await dispatch('validateFolderNameUnique', name)
      if (!success) return { success }

      try {
        const { data } = (await FolderApi.update(folderId, { name })) as { data: JsonAPIResource<Folder> }
        const folder = data.attributes
        commit('CACHE_FOLDERS', { folders: [folder] })
        return { success: true }
      } catch (error) {
        void dispatch('genericFetchError', { error }, { root: true })
        return { success: false }
      }
    },
    async deleteFolder({ commit, dispatch }, { folderId }): Promise<{ success: boolean }> {
      try {
        await FolderApi.delete(folderId)
        commit('REMOVE_FOLDER_FOR_ALL_USERS', { folderId })
        return { success: true }
      } catch (error) {
        void dispatch('genericFetchError', { error }, { root: true })
        return { success: false }
      }
    },

    // BOOKMARKS

    async fetchBookmarks({ getters, commit }, { wallId }): Promise<void> {
      if (!assertPresent({ wallId })) return

      const response = await fetchJson(`/api/1/bookmarks`, {
        method: HTTPMethod.get,
        query: { wallId },
      })

      const folderIds = response.data.map((record) => record.attributes.folderId)
      folderIds.forEach((folderId) => {
        const accountKey = { type: 'user', id: getters.activeUserId }
        const collectionKey = folderId
          ? { typeKey: 'folderId', indexKey: folderId }
          : { typeKey: 'filter', indexKey: 'favorites' }
        commit('ADD_WALL_IDS_TO_COLLECTION', { accountKey, collectionKey, wallIds: [wallId] })
      })
    },
    async addBookmark({ getters, commit }, options): Promise<void> {
      // folderId is allowed to be blank
      const folderId = options.folderId
      const wallId = options.wallId
      if (!assertPresent({ wallId })) return

      await fetchJson(`/api/1/bookmarks`, {
        method: HTTPMethod.post,
        jsonData: { wallId, folderId },
      })

      const accountKey = { type: 'user', id: getters.activeUserId }
      const collectionKey = folderId
        ? { typeKey: 'folderId', indexKey: folderId }
        : { typeKey: 'filter', indexKey: 'favorites' }
      commit('ADD_WALL_IDS_TO_COLLECTION', { accountKey, collectionKey, wallIds: [wallId] })
    },
    async removeBookmark({ getters, commit }, options): Promise<void> {
      // folderId is allowed to be blank
      const folderId = options.folderId
      const wallId = options.wallId
      if (!assertPresent({ wallId })) return

      await fetchJson(`/api/1/bookmarks`, {
        method: HTTPMethod.delete,
        jsonData: { wallId, folderId },
      })

      const accountKey = { type: 'user', id: getters.activeUserId }
      const collectionKey = folderId
        ? { typeKey: 'folderId', indexKey: folderId }
        : { typeKey: 'filter', indexKey: 'favorites' }
      commit('REMOVE_WALL_ID_FROM_COLLECTION', { accountKey, collectionKey, wallId })
    },

    // USER GROUPS

    async fetchUserGroupFolders({ getters, commit, dispatch }): Promise<void> {
      try {
        const response = await UserGroupsApi.fetchUserGroupFolders()

        const userGroupFolders = response.data as Array<JsonAPIResource<UserGroupFolderApiResponse>>
        const userId = getters.activeUserId
        const accountKey = { type: 'user', id: userId }
        const groups: Array<{ id: UserGroupId; name: string }> = []

        userGroupFolders.forEach(({ attributes: { id, name, wallIds } }) => {
          groups.push({ id, name })
          const collectionKey: CollectionKey = { typeKey: CollectionKeyTypes.GroupFolderId, indexKey: id }
          commit('SET_COLLECTION_WALL_IDS', { accountKey, collectionKey, wallIds })
        })

        commit('CACHE_GROUPS', { groups })
        const groupIds = groups.map((group) => group.id)
        commit('UPDATE_GROUP_LIST_FOR_USER', { userId, groupIds })
      } catch (error) {
        void dispatch('genericFetchError', { error }, { root: true })
      }
    },

    // FETCH COLLECTION WALLS

    /**
     * Fetches current and subsequent pages of walls for given collection.
     *
     * Since we need to obtain a cursor for a page from the preceding it, we have to fetch them sequentially,
     * i.e. in a "chain".
     */
    async fetchCollectionWalls(
      { commit, dispatch, getters, rootGetters },
      {
        accountKey,
        collectionKey,
        accumulatedWallIds,
        pageSize = 100,
        hasNextPage,
        nextPage,
        fetchNextPage = true,
      }: {
        accountKey: AccountKey
        collectionKey: CollectionKey
        accumulatedWallIds: Wall[]
        pageSize: number
        hasNextPage: boolean
        nextPage: number | null
        fetchNextPage: boolean
      },
    ): Promise<void> {
      devLog('fetchCollectionWalls', { accountKey, collectionKey, accumulatedWallIds, hasNextPage, nextPage })

      let isChainHalted = false
      const haltChain = (): boolean => (isChainHalted = true)
      const isFirstFetchInChain = nextPage === 0 || nextPage == null
      if (isFirstFetchInChain) commit('FETCH_WALLS_START', { accountKey, collectionKey })
      const promise = async (): Promise<void> => {
        try {
          const urlObject: { path: string; search: Record<string, any> } = {
            path: '/api/7/walls',
            search: {},
          }

          if (isAppUsing('userGroups') && collectionKey.typeKey === CollectionKeyTypes.GroupFolderId) {
            urlObject.search.userGroupId = collectionKey.indexKey
          } else if (collectionKey.typeKey === CollectionKeyTypes.FolderId) {
            urlObject.search.folderId = collectionKey.indexKey
          } else if (collectionKey.typeKey === CollectionKeyTypes.Filter) {
            urlObject.search.filter = collectionKey.indexKey

            if (accountKey.type === 'user') {
              urlObject.search.userId = accountKey.id
            } else if (accountKey.type === 'library') {
              if (collectionKey.indexKey === 'trashed') {
                urlObject.search.userId = rootGetters.currentUser?.id
              }
              urlObject.search.libraryId = accountKey.id
            }

            if (getters.activeSharedCollectionAccountKey?.type === 'user') {
              urlObject.search.targetUserId = getters.activeSharedCollectionAccountKey?.id
            } else if (getters.activeSharedCollectionAccountKey?.type === 'library') {
              urlObject.search.targetLibraryId = getters.activeSharedCollectionAccountKey?.id
            }
          }

          // match page[size] and page[number] in backend
          urlObject.search['page[size]'] = pageSize
          urlObject.search['page[number]'] = nextPage == null ? 0 : nextPage

          const sortAttributeMapping = {
            lastPresentAt: '-last_present_at',
            updatedAt: '-updated_at',
            title: 'title',
            createdAt: '-created_at',
            trashedAt: '-trashed_at',
          }
          urlObject.search.sort = sortAttributeMapping[getters.activeCollectionDateSortAttribute]

          // Fetch current page
          const result = await fetchJson(transformCurrentUrl({}, urlObject))

          const wallsNodes = result.data as Array<JsonAPIResource<Wall>>
          const walls = wallsNodes.map((wall) => wall.attributes)

          const wallIds = walls.map((wall) => wall.id)

          // Show new walls as they come. Deleted walls will be hidden later after the last fetch in the chain.
          commit('CACHE_WALLS', { walls })
          commit('ADD_WALL_IDS_TO_COLLECTION', { accountKey, collectionKey, wallIds })

          const hasNextPage: boolean | undefined = result?.meta?.page?.hasNextPage
          const newAccumulatedWallIds = uniq([...(accumulatedWallIds ?? []), ...wallIds])

          if (!isChainHalted && Boolean(hasNextPage) && fetchNextPage) {
            void dispatch('fetchCollectionWalls', {
              accountKey,
              collectionKey,
              accumulatedWallIds: newAccumulatedWallIds,
              pageSize,
              hasNextPage,
              nextPage: result?.meta?.page?.nextPage,
              fetchNextPage,
            })
          } else {
            commit('FETCH_WALLS_COMPLETE', { accountKey, collectionKey })
            // If walls have been removed from current collection, reflect that fact.
            commit('SET_COLLECTION_WALL_IDS', { accountKey, collectionKey, wallIds: newAccumulatedWallIds })
          }
        } catch (error) {
          captureException(error)
          commit('FETCH_WALLS_ERROR', { accountKey, collectionKey })
        }
      }
      return await q.enqueue('fetchCollectionWalls', promise, { metadata: { accountKey, collectionKey, haltChain } })
    },

    async fetchActiveCollectionWalls({ getters, dispatch }): Promise<void> {
      const accountKey: AccountKey = getters.activeAccountKey
      const collectionKey: CollectionKey = getters.activeCollectionKey

      const fetchCollectionWallsParams: {
        accountKey: AccountKey
        collectionKey: CollectionKey
        pageSize?: number
        fetchNextPage?: boolean
      } = {
        accountKey,
        collectionKey,
      }

      // for certain collections we only fetch a max number of walls
      const fetchLimits = getCollectionFetchLimits(collectionKey)
      if (fetchLimits != null) {
        // use a single fetch if such a limit exists
        fetchCollectionWallsParams.pageSize = fetchLimits
        fetchCollectionWallsParams.fetchNextPage = false
      }

      await dispatch('fetchCollectionWalls', fetchCollectionWallsParams)
    },

    async ensureWallsFetchedForCollection(
      { dispatch, getters },
      { collectionKey }: { collectionKey: CollectionKey },
    ): Promise<void> {
      const accountKey: AccountKey = { id: getters.activeUserId, type: 'user' }
      const isCollectionFetched = getters.isCollectionFetched(collectionKey)
      const isCollectionFetching = getters.isCollectionFetching(collectionKey)
      if (!isCollectionFetched && !isCollectionFetching) {
        await dispatch('fetchCollectionWalls', { accountKey, collectionKey })
      }
    },
    /**
     * Makes wall fetch chains "interruptable".
     *
     * If fetchCollectionWalls is not halted, one fetchCollectionWalls chain will only start after
     * the previous one has completed or errored. If the user has many pages of walls, we don't want
     * to wait for the fetching of the previous collection to complete before fetching the currently
     * visible one.
     */
    async haltFetchCollectionWalls({ commit }): Promise<void> {
      const key = 'fetchCollectionWalls'
      // Plant the unqueueKey event first so that the next promise in line doesn't start executing
      // once we resolve the current promise
      const unqueueKeyPromise = q.unqueueKey(key)

      // Give the halt signal to the promise that is in flight.
      if (q.currentKey === key) {
        const inFlightPromise = q.resolveCurrentPromiseNow([])
        inFlightPromise?.metadata?.haltChain()
      }

      // Terminate queued fetches
      return await unqueueKeyPromise.then((unqueuedFetches) => {
        unqueuedFetches.forEach((unqueuedFetch) => {
          const { accountKey, collectionKey } = unqueuedFetch.metadata
          commit('FETCH_WALLS_HALT', { accountKey, collectionKey })
        })
      })
    },

    async fetchActiveCollectionWallsNow({ dispatch }): Promise<void> {
      await dispatch('haltFetchCollectionWalls')
      void dispatch('fetchActiveCollectionWalls')
    },

    // CHANGE VIEW

    switchAccountKey({ commit, rootGetters }, accountKey: AccountKey): void {
      const collectionKey = { typeKey: 'filter', indexKey: 'all' }
      commit('SET_ACCOUNT_COLLECTION_CURSOR', { accountKey, collectionKey })
    },
    switchCollectionKey({ commit, getters }, collectionKey: CollectionKey): void {
      let accountKey = getters.activeAccountKey

      const invalidCollectionForLibrary =
        accountKey.type === 'library' &&
        (collectionKey.typeKey !== 'filter' ||
          !(getters.libraryAccountFilters as WallsFilter[]).includes(collectionKey.indexKey))

      if (invalidCollectionForLibrary) {
        accountKey = { type: 'user', id: getters.activeUserId }
      }

      commit('SET_ACCOUNT_COLLECTION_CURSOR', { accountKey, collectionKey })
    },
    switchGalleryViz({ commit, getters, dispatch }, viz: WallViz): void {
      commit('SET_GALLERY_VIZ', viz)
      if (getters.activeCollectionKey.indexKey === 'gallery') return
      dispatch('switchCollectionKey', { typeKey: 'filter', indexKey: 'gallery' })
    },
    sortWalls({ commit, getters }, sortAttribute: WallSortAttribute): void {
      if (sortAttribute === getters.activeSortAttribute) {
        const otherSortDirection = getters.activeSortDirection === 'asc' ? 'desc' : 'asc'
        commit('SET_ACTIVE_SORT_DIRECTION', otherSortDirection)
      } else {
        commit('SET_ACTIVE_SORT_ATTRIBUTE', sortAttribute)
      }
    },

    // WALL ACTIONS

    createWall({ dispatch, getters }): void {
      // If the active collection is a folder, user might expect to see the made wall there.
      // To avoid confusion, make sure that the newly created wall is visible when the user
      // goes back to the dashboard or collection viewer.
      dispatch('switchCollectionKey', { typeKey: 'filter', indexKey: 'made' })

      const electronApp = fetchElectronBridge()

      const createWallPath =
        getters.activeAccountKey?.type === 'library'
          ? `/create?library_id=${getters.activeAccountKey.id}&back=1&old_layout_picker=1`
          : `/create?back=1&old_layout_picker=1`

      if (electronApp.openNewWindow) {
        // Open the Make a padlet page in a new window in the electron app
        electronApp.openNewWindow(buildUrlFromPath(createWallPath))
      } else {
        navigateTo(createWallPath)
      }
    },
    copyActiveWallLinkToClipboard({ getters, dispatch }): void {
      copyToClipboard(`${currentHostWithProtocol()}/${getters.activeWall.address}`)
      dispatch('unsetActiveWall')
    },
    openActiveWallSlideshow({ getters, dispatch }): void {
      if (getters.activeWall.links.show == null) {
        return
      }

      const coverPageUrl = buildSlideshowLink(getters.activeWall.links.show, PageType.Cover)

      navigateTo(coverPageUrl, { target: '_blank' })
      void dispatch('unsetActiveWall')
    },
    async removeActiveWallRecents({ getters, dispatch }): Promise<void> {
      const promise = dispatch('removeWallRecents', {
        wallId: getters.activeWallId,
        accountKey: { type: 'user', id: getters.activeUserId },
      })
      await dispatch('unsetActiveWall')
      return await promise
    },
    async removeWallRecents(
      { commit, dispatch },
      { wallId, accountKey }: { wallId: WallId; accountKey: AccountKey },
    ): Promise<void> {
      const promise = async (): Promise<void> => {
        try {
          await fetchResponse(`/api/1/walls/${wallId}/remove_from_recents`, {
            method: HTTPMethod.delete,
            headers: {
              'X-CSRF-Token': getCsrfToken(),
            },
          })
          commit('UPDATE_WALL_ATTRIBUTE', { wallId, targetAttribute: 'isInRecents', newAttributeValue: false })
          commit('REMOVE_WALL_ID_FROM_COLLECTION', {
            accountKey,
            collectionKey: { typeKey: 'filter', indexKey: 'combined_recents' },
            wallId,
          })
          await dispatch(
            'snackbar',
            { notificationType: SnackbarNotificationType.success, message: __('Padlet removed from recents') },
            { root: true },
          )
        } catch (error) {
          await dispatch('genericFetchError', { error }, { root: true })
        }
      }
      return await q.enqueue('removeWallRecents', promise)
    },
    async archiveWall({ commit, dispatch }, { wallId, accountKey }): Promise<void> {
      const promise = async (): Promise<void> => {
        try {
          await WallApi.archive(wallId)
          commit('REMOVE_WALL_ID_FROM_ALL_COLLECTIONS', { wallId })

          const collectionKey: CollectionKey = { typeKey: 'filter', indexKey: 'archived' }
          commit('ADD_WALL_IDS_TO_COLLECTION', { accountKey, collectionKey, wallIds: [wallId] })
        } catch (error) {
          void dispatch('genericFetchError', { error }, { root: true })
        }
      }
      return await q.enqueue('archiveWall', promise)
    },
    async archiveActiveWall({ getters, dispatch }): Promise<void> {
      const promise = dispatch('archiveWall', { wallId: getters.activeWallId, accountKey: getters.activeAccountKey })
      void dispatch('unsetActiveWall')
      return await promise
    },
    async unarchiveWall({ commit, dispatch }, { wallId }): Promise<void> {
      const promise = async (): Promise<void> => {
        try {
          await WallApi.unarchive(wallId)
          commit('REMOVE_WALL_ID_FROM_ALL_COLLECTIONS', { wallId })
        } catch (error) {
          void dispatch('genericFetchError', { error }, { root: true })
        }
      }
      return await q.enqueue('unarchiveWall', promise)
    },
    async unarchiveActiveWall({ getters, dispatch }): Promise<void> {
      const promise = dispatch('unarchiveWall', { wallId: getters.activeWallId })
      dispatch('unsetActiveWall')
      return await promise
    },
    async deleteWall({ commit, dispatch }, { wallId }): Promise<void> {
      const promise = async (): Promise<void> => {
        try {
          await WallApi.delete(wallId)
          commit('REMOVE_WALL_ID_FROM_ALL_COLLECTIONS', { wallId })
        } catch (error) {
          void dispatch('genericFetchError', { error }, { root: true })
        }
      }
      return await q.enqueue('deleteWall', promise)
    },
    async deleteActiveWall({ getters, dispatch }): Promise<void> {
      const promise = dispatch('deleteWall', { wallId: getters.activeWallId })
      dispatch('unsetActiveWall')
      return await promise
    },
    async trashWall({ commit, dispatch }, { wallId }): Promise<void> {
      const promise = async (): Promise<void> => {
        await WallApi.trash(wallId)
        commit('REMOVE_WALL_ID_FROM_ALL_COLLECTIONS', { wallId })
      }
      return await q.enqueue('trashWall', promise)
    },
    async trashActiveWall({ getters, dispatch }): Promise<void> {
      const promise = dispatch('trashWall', { wallId: getters.activeWallId })
      void dispatch('unsetActiveWall')
      return await promise
    },
    async untrashWall({ commit, dispatch }, { wallId }): Promise<void> {
      const promise = async (): Promise<void> => {
        await WallApi.untrash(wallId)
        commit('REMOVE_WALL_ID_FROM_ALL_COLLECTIONS', { wallId })
      }
      return await q.enqueue('untrashWall', promise)
    },
    async untrashActiveWall({ getters, dispatch }): Promise<void> {
      const promise = dispatch('untrashWall', { wallId: getters.activeWallId })
      void dispatch('unsetActiveWall')
      return await promise
    },

    async permanentlyDeleteWall({ commit, dispatch }, { wallId }): Promise<void> {
      const promise = async (): Promise<void> => {
        await WallApi.permanentlyDelete(wallId)
        commit('REMOVE_WALL_ID_FROM_ALL_COLLECTIONS', { wallId })
      }
      return await q.enqueue('permanentlyDeleteWall', promise)
    },
    async permanentlyDeleteActiveWall({ getters, dispatch }): Promise<void> {
      const promise = dispatch('permanentlyDeleteWall', { wallId: getters.activeWallId })
      void dispatch('unsetActiveWall')
      return await promise
    },

    async leaveWall({ commit, dispatch, getters, rootGetters }, { wallId }): Promise<void> {
      const promise = async (): Promise<void> => {
        try {
          await PadletApi.collaboratorLeavePadlet(wallId)

          if (rootGetters.xSharedCollections) {
            const accountKey = { type: 'user', id: getters.activeUserId }
            commit('REMOVE_WALL_ID_FROM_COLLECTION', {
              accountKey,
              collectionKey: { typeKey: 'filter', indexKey: 'combined_shared' },
              wallId,
            })
            if (getters.activeSharedCollectionAccountKey?.type === 'library') {
              commit('REMOVE_WALL_ID_FROM_COLLECTION', {
                accountKey,
                collectionKey: { typeKey: 'filter', indexKey: 'combined_shared_library' },
                wallId,
              })
            } else if (getters.activeSharedCollectionAccountKey?.type === 'user') {
              commit('REMOVE_WALL_ID_FROM_COLLECTION', {
                accountKey,
                collectionKey: { typeKey: 'filter', indexKey: 'combined_shared_user' },
                wallId,
              })
            }
          } else {
            commit('UPDATE_WALL_ATTRIBUTE', {
              wallId,
              targetAttribute: 'collaboratorRight',
              newAttributeValue: 0,
            })
          }

          dispatch(
            'snackbar',
            { notificationType: SnackbarNotificationType.success, message: __('Padlet left') },
            { root: true },
          )
        } catch (error) {
          dispatch('genericFetchError', { error }, { root: true })
        }
      }
      return await q.enqueue('leaveWall', promise)
    },
    async leaveActiveWall({ dispatch, getters }): Promise<void> {
      const promise = dispatch('leaveWall', { wallId: getters.activeWallId })
      dispatch('unsetActiveWall')
      return await promise
    },

    // SEARCH
    setActiveSearchQuery({ commit }, { activeSearchQuery }): void {
      commit('SET_ACTIVE_SEARCH_QUERY', { activeSearchQuery })
    },
    async fetchSearchResults({ state, getters, rootGetters, commit }, { activeSearchQuery }): Promise<void> {
      const userId = getters.activeUserId
      if (userId == null) return

      const collectionKey: CollectionKey = {
        typeKey: 'search',
        indexKey: activeSearchQuery,
      }

      setAccountCollectionValue(state.isUsersCollectionsFetchErrored, userId, collectionKey, false)

      commit('SET_ACTIVE_SEARCH_QUERY', { activeSearchQuery })

      if (state.activeSearchQuery === '') return

      try {
        const searchEndpoint = 'api/1/search/padlets.json'
        setAccountCollectionValue(state.isUsersCollectionsFetching, userId, collectionKey, true)
        const searchResults = await fetchJson(searchEndpoint, {
          query: {
            q: activeSearchQuery,
          },
        })
        const walls = searchResults.hits

        const wallsToCache = walls.filter((wall) => state.wallsById[wall.id] == null)
        commit('CACHE_WALLS', { walls: wallsToCache })

        const accountKey: AccountKey = { type: 'user', id: userId }
        const wallIds = walls.map((wall) => wall.id)
        commit('SET_COLLECTION_WALL_IDS', { accountKey, collectionKey, wallIds })
        setAccountCollectionValue(state.isUsersCollectionsFetched, userId, collectionKey, true)
      } catch (e) {
        setAccountCollectionValue(state.isUsersCollectionsFetchErrored, userId, collectionKey, true)
      } finally {
        setAccountCollectionValue(state.isUsersCollectionsFetching, userId, collectionKey, false)
      }
    },

    // SHARED
    async fetchSharedCollections({ rootGetters, dispatch, commit }): Promise<void> {
      try {
        const response = await fetchJson(`api/1/libraries/combined_shared/collections`, {
          query: {
            userId: rootGetters.currentUser?.id,
          },
        })
        const sharedCollections = response.data.map((item) => {
          return { ...item.attributes }
        })
        commit('SET_SHARED_COLLECTIONS', { sharedCollections })
      } catch (error) {
        dispatch('genericFetchError', { error }, { root: true })
      }
    },
    viewSharedCollection(
      { dispatch, commit },
      sharedCollectionPayload: { collectionKey: CollectionKey; sharedCollectionAccountKey: SharedCollectionAccountKey },
    ): void {
      dispatch('switchCollectionKey', sharedCollectionPayload.collectionKey)
      commit('SET_SHARED_COLLECTION_ACTIVE_KEY', {
        sharedCollectionAccountKey: sharedCollectionPayload.sharedCollectionAccountKey,
      })

      dispatch('fetchActiveCollectionWallsNow')
    },

    // LOCAL CACHE

    saveWallsLocally({ state, getters }): void {
      let wallsById = cloneDeep(state.wallsById)
      let usersCollectionsWallIds = cloneDeep(state.usersCollectionsWallIds)
      let librariesCollectionsWallIds = cloneDeep(state.librariesCollectionsWallIds)

      // Prune if payload is too big
      const maxWalls = 90
      if (Object.keys(wallsById).length > maxWalls) {
        const walls = getters.activeCollectionWalls.slice(0, maxWalls)
        wallsById = keyBy(walls, 'id')

        const pruneIds = (item): any => {
          if (Array.isArray(item)) return item.filter((id) => wallsById[id])
          if (typeof item === 'object') return mapValues(item, pruneIds)
          return item
        }
        usersCollectionsWallIds = pruneIds(usersCollectionsWallIds)
        librariesCollectionsWallIds = pruneIds(librariesCollectionsWallIds)
      }

      devLog('saveWallsLocally', { wallsById, usersCollectionsWallIds, librariesCollectionsWallIds })
      safeStorage.setItem(`wallsById`, asciiSafeStringify(wallsById))
      safeStorage.setItem(`usersCollectionsWallIds`, asciiSafeStringify(usersCollectionsWallIds))
      safeStorage.setItem(`librariesCollectionsWallIds`, asciiSafeStringify(librariesCollectionsWallIds))
    },
    loadWallsFromLocalCache({ commit }): void {
      const wallsById = safeStorage.getItem('wallsById')
      const usersCollectionsWallIds = safeStorage.getItem('usersCollectionsWallIds')
      const librariesCollectionsWallIds = safeStorage.getItem('librariesCollectionsWallIds')

      // clear local cache right after loading to prevent data staleness
      safeStorage.removeItem('wallsById')
      safeStorage.removeItem('usersCollectionsWallIds')
      safeStorage.removeItem('librariesCollectionsWallIds')

      // If strings are found in local storage, we expect them to be parseable.
      const stateToHydrate: Partial<CollectionsStoreModule> = {}
      if (wallsById) stateToHydrate.wallsById = JSON.parse(wallsById)
      if (usersCollectionsWallIds) stateToHydrate.usersCollectionsWallIds = JSON.parse(usersCollectionsWallIds)
      if (librariesCollectionsWallIds)
        stateToHydrate.librariesCollectionsWallIds = JSON.parse(librariesCollectionsWallIds)
      commit('INITIALIZE_STATE', stateToHydrate)
    },
  },
}

export default collectionsStore
